import React, { useId } from "react";

/**
 * Campo de formulario con etiqueta correctamente asociada.
 *
 * Todos los formularios públicos —contacto, misionero digital e inscripción—
 * escribían `<label className="block …">Nombre *</label>` suelto, sin `htmlFor`
 * ni `id` en el input y sin anidarlos. Consecuencias reales: pulsar sobre la
 * etiqueta no llevaba el foco al campo, y un lector de pantalla anunciaba
 * «cuadro de edición» sin decir de qué.
 *
 * Se usa `useId()` para generar identificadores únicos y estables, de modo que
 * dos formularios en la misma página nunca colisionen.
 */
export default function Field({
  label,
  required = false,
  hint,
  error,
  as = "input",
  className = "",
  children,
  ...rest
}) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined;

  const clasesControl =
    "w-full px-4 py-2.5 rounded-xl border border-input bg-background text-sm focus-ring " +
    (as === "textarea" ? "resize-none " : "") +
    className;

  const propsComunes = {
    id,
    required,
    "aria-describedby": describedBy,
    "aria-invalid": error ? true : undefined,
    className: clasesControl,
    ...rest,
  };

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-semibold mb-1.5">
        {label}
        {required && (
          <>
            {" "}
            <span className="text-primary" aria-hidden="true">*</span>
            <span className="sr-only">(obligatorio)</span>
          </>
        )}
      </label>

      {as === "textarea" ? (
        <textarea {...propsComunes} />
      ) : as === "select" ? (
        <select {...propsComunes}>{children}</select>
      ) : (
        <input {...propsComunes} />
      )}

      {hint && (
        <p id={hintId} className="text-xs text-muted-foreground mt-1.5">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="text-xs text-destructive mt-1.5">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * Mensaje de error de formulario.
 *
 * `role="alert"` es lo que faltaba: sin él, un fallo de validación aparecía en
 * pantalla sin que ningún lector de pantalla lo anunciara.
 */
export function FormError({ children, ...rest }) {
  if (!children) return null;
  return (
    <p
      role="alert"
      className="text-sm text-destructive bg-destructive/10 px-4 py-2.5 rounded-xl"
      {...rest}
    >
      {children}
    </p>
  );
}
